import React from "react";
import PropTypes from "prop-types";
// @material-ui/core components
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
// @material-ui/icons components
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import ArrowUpward from "@material-ui/icons/ArrowUpward";
import EmojiEvents from "@material-ui/icons/EmojiEvents";
import GroupAdd from "@material-ui/icons/GroupAdd";
import Home from "@material-ui/icons/Home";
import InsertChartOutlined from "@material-ui/icons/InsertChartOutlined";
import PieChart from "@material-ui/icons/PieChart";

// core components
import CardStats from "components/Cards/Dashboard/CardStats.js";

const StatsHeader = ({ section, subsection }) => {
  const theme = useTheme();
  return (
    <>
      <Box
        sx={{
          position: "relative",
          backgroundColor: theme.palette.info.main,
          paddingBottom: "8rem",
        }}
      >
        <Container
          maxWidth={false}
          component={Box}
          sx={{
            [theme.breakpoints.up("md")]: {
              paddingLeft: "30px",
              paddingRight: "30px",
            },
          }}
        >
          <Grid
            container
            component={Box}
            sx={{
              alignItems: "center",
              paddingTop: "1.5rem",
              paddingBottom: "1.5rem",
            }}
          >
            <Grid
              item
              xs={7}
              lg={6}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Typography
                variant="h2"
                component="h6"
                sx={{
                  display: "inline-block",
                  marginBottom: 0,
                  color: theme.palette.white.main,
                }}
              >
                {section}
              </Typography>
              <Breadcrumbs
                separator="-"
                aria-label="breadcrumb"
                sx={{
                  display: "none",
                  lineHeight: "1.5!important",
                  [theme.breakpoints.up("md")]: {
                    marginLeft: "1.5rem",
                    display: "inline-block",
                  },
                  "& .MuiBreadcrumbs-ol": {
                    background: "transparent !important",
                    marginBottom: "0 !important",
                  },
                  "& .MuiBreadcrumbs-li a": {
                    color: theme.palette.gray[100],
                    textDecoration: "none",
                    fontWeight: 600,
                    "&:hover": {
                      color: theme.palette.white.main,
                      textDecoration: "none",
                    },
                  },
                  "& .MuiBreadcrumbs-separator": {
                    color: theme.palette.white.main + "!important",
                  },
                }}
              >
                <Link
                  color="inherit"
                  href="/"
                  onClick={(e) => e.preventDefault()}
                >
                  <Box
                    component={Home}
                    sx={{
                      width: "1.25rem!important",
                      height: "1.25rem!important",
                      position: "relative",
                    }}
                  />
                </Link>
                <Link
                  color="inherit"
                  href="/getting-started/installation/"
                  onClick={(e) => e.preventDefault()}
                >
                  {subsection}
                </Link>
                <Typography
                  component="span"
                  sx={{
                    fontSize: ".875rem",
                    color: theme.palette.gray[300],
                    fontWeight: 600,
                  }}
                >
                  {section}
                </Typography>
              </Breadcrumbs>
            </Grid>
            <Grid
              item
              xs={5}
              lg={6}
              component={Box}
              sx={{ textAlign: "right" }}
            >
              <Button
                variant="neutral"
                size="small"
                sx={{ marginRight: ".75rem" }}
              >
                New
              </Button>
              <Button variant="neutral" size="small">
                Filters
              </Button>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xl={3} lg={6} xs={12}>
              <CardStats
                subtitle="Traffic"
                title="350,897"
                icon={InsertChartOutlined}
                color="bgError"
                footer={
                  <>
                    <Box
                      component="span"
                      sx={{
                        fontSize: ".875rem",
                        color: theme.palette.success.main,
                        marginRight: ".5rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        component={ArrowUpward}
                        sx={{
                          width: "1.25rem!important",
                          height: "1.25rem!important",
                          marginLeft: "-.25rem",
                        }}
                      />{" "}
                      3.48%
                    </Box>
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      Since last month
                    </Box>
                  </>
                }
              />
            </Grid>
            <Grid item xl={3} lg={6} xs={12}>
              <CardStats
                subtitle="New users"
                title="2,356"
                icon={PieChart}
                color="bgWarning"
                footer={
                  <>
                    <Box
                      component="span"
                      sx={{
                        fontSize: ".875rem",
                        color: theme.palette.error.main,
                        marginRight: ".5rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        component={ArrowDownward}
                        sx={{
                          width: "1.25rem!important",
                          height: "1.25rem!important",
                          marginLeft: "-.25rem",
                        }}
                      />{" "}
                      3.48%
                    </Box>
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      Since last week
                    </Box>
                  </>
                }
              />
            </Grid>
            <Grid item xl={3} lg={6} xs={12}>
              <CardStats
                subtitle="Sales"
                title="924"
                icon={GroupAdd}
                color="bgSuccess"
                footer={
                  <>
                    <Box
                      component="span"
                      sx={{
                        fontSize: ".875rem",
                        color: theme.palette.warning.main,
                        marginRight: ".5rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        component={ArrowDownward}
                        sx={{
                          width: "1.25rem!important",
                          height: "1.25rem!important",
                          marginLeft: "-.25rem",
                        }}
                      />{" "}
                      1.10%
                    </Box>
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      Since yesterday
                    </Box>
                  </>
                }
              />
            </Grid>
            <Grid item xl={3} lg={6} xs={12}>
              <CardStats
                subtitle="Performance"
                title="49,65%"
                icon={EmojiEvents}
                color="bgPrimary"
                footer={
                  <>
                    <Box
                      component="span"
                      sx={{
                        fontSize: ".875rem",
                        color: theme.palette.success.main,
                        marginRight: ".5rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        component={ArrowUpward}
                        sx={{
                          width: "1.25rem!important",
                          height: "1.25rem!important",
                          marginLeft: "-.25rem",
                        }}
                      />{" "}
                      10%
                    </Box>
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      Since last month
                    </Box>
                  </>
                }
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

StatsHeader.propTypes = {
  section: PropTypes.string,
  subsection: PropTypes.string,
};

export default StatsHeader;
